import { ReactNode, MouseEvent, useRef, useEffect } from 'react'

import useDispatch from 'hooks/useDispatch'
import { fetchCurrentUser } from 'actions/authActions'
import { sendNotification } from 'actions/notificationActions'

import AuthToken from './AuthToken'
import FacebookIcon from '../../icons/FacebookCircle'
import { LoginSocial } from '../styles'

type FacebookLoginProps = {
  children: ReactNode
  disabled?: boolean
  // When true (standalone /login and /signup pages), skip the JS popup and let
  // the form POST drive a full server-side OmniAuth dance, matching the other
  // providers. False for the AuthModal, where the popup keeps the parent put.
  serverSide?: boolean
  onSuccess?: () => void
  onConfirmEmail?: () => void
}

const POPUP_NAME = 'fb-oauth'
const POPUP_FEATURES =
  'width=600,height=700,resizable=yes,scrollbars=yes,status=yes'
const POLL_MS = 500

const FacebookLogin = ({
  children,
  disabled,
  serverSide,
  onSuccess,
  onConfirmEmail,
}: FacebookLoginProps) => {
  const dispatch = useDispatch()
  const pollRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (pollRef.current != null) window.clearInterval(pollRef.current)
    },
    [],
  )

  const finalize = (popup: Window | null) => {
    // Read the popup's final path BEFORE closing it — used as a fallback
    // navigation target when no JS callback is registered (e.g. the standalone
    // /login and /signup pages, which previously relied on the server-side
    // redirect to navigate).
    let fallbackPath: string | null = null
    if (popup && !popup.closed) {
      try {
        const pathname = popup.location.pathname
        if (!pathname.startsWith('/omniauth/')) {
          fallbackPath = pathname + popup.location.search
        }
      } catch {
        // cross-origin at finalize time shouldn't happen, but ignore if it does
      }
      popup.close()
    }

    // @ts-ignore -- redux-promise-middleware resolves to the fulfilled action
    dispatch(fetchCurrentUser()).then((action: any) => {
      const user = action?.value?.data
      if (!user) {
        dispatch(sendNotification('Facebook login failed', 'error'))
        return
      }
      const isReturning = user.email && user.sign_in_count > 1
      const callback = isReturning ? onSuccess : onConfirmEmail
      if (callback) {
        callback()
      } else if (fallbackPath) {
        window.location.assign(fallbackPath)
      }
    })
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    const form = event.currentTarget.form
    if (!form) return

    const popup = window.open('', POPUP_NAME, POPUP_FEATURES)
    if (!popup) return // popup blocked — let the form submit inline as fallback

    event.preventDefault()
    form.target = POPUP_NAME
    form.submit()
    form.target = ''

    if (pollRef.current != null) window.clearInterval(pollRef.current)
    pollRef.current = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(pollRef.current!)
        pollRef.current = null
        finalize(null)
        return
      }
      try {
        const href = popup.location.href
        // Initial popup state — wait for the form POST to navigate it.
        if (href === 'about:blank' || href === '') return
        if (popup.location.origin !== window.location.origin) return
        // Same-origin, non-blank: popup has finished navigating (success,
        // confirm-email, or a server-side error page). Browsers don't expose
        // /omniauth/facebook as a transient location during a 302, so any
        // same-origin URL here is the final destination.
        window.clearInterval(pollRef.current!)
        pollRef.current = null
        finalize(popup)
      } catch {
        // Cross-origin access throws — popup is still on facebook.com.
      }
    }, POLL_MS)
  }

  return (
    <form action="/omniauth/facebook" method="post">
      <AuthToken />
      <LoginSocial
        disabled={disabled}
        type="submit"
        onClick={serverSide ? undefined : handleClick}
        className="qa-facebook-login"
      >
        <FacebookIcon />
        {children}
      </LoginSocial>
    </form>
  )
}

export default FacebookLogin
